import { useState } from 'react';
import { Link } from 'react-router-dom';
import { detailOr } from '@/services/api';
import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import type { EnrolActionProps } from '@/features/publicsite/types/catalog/EnrolActionProps';
import { publicCatalog } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * The card's call to action, which is not the same thing for a signed-out visitor as for a
 * signed-in one.
 *
 * It used to be one anchor to /saml/authenticate/mpass for everybody, so a visitor who was
 * already signed in pressed "Înscrie-te", went through MPass a second time and landed on the
 * cabinet with no enrolment made. `POST /api/v1/training/sessions/{id}/enrollments` has existed
 * on the backend all along with no caller anywhere in the client — the only thing that ever
 * reached it was training.spec.ts, through the request API. That is why /app/instruiri could not
 * show a row no matter what an administrator created: nothing could produce one.
 */
export function EnrolAction({ sessionId, signedIn, enrolled, onEnrolled }: EnrolActionProps) {
  const { t } = useTranslation(publicCatalog);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!signedIn) {
    // A real navigation: /saml/authenticate/mpass is a server redirect out to the IdP.
    return (
      <a className="btn btn--primary btn--md" href="/saml/authenticate/mpass">
        <img className="btn__logo" src="/img/gov/mpass.svg" alt="" aria-hidden="true" />
        <span>{t('trainingsEnrol')}</span>
      </a>
    );
  }

  if (enrolled) {
    return (
      <p className="muted" role="status">
        {t('trainingsEnrolled')} <Link to="/app/instruiri">{t('trainingsEnrolledLink')}</Link>
      </p>
    );
  }

  const enrol = async () => {
    setBusy(true);
    setError('');
    try {
      await publicCatalogApi.enrol(sessionId);
      onEnrolled(sessionId);
    } catch (e) {
      // The backend refuses for three reasons — already enrolled, session full, session not open
      // — and each arrives as a 409 whose `detail` is the sentence to show. Anything else gets the
      // generic line rather than a status code the visitor cannot act on.
      setError(detailOr(e, t('trainingsEnrolError')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <button
        className="btn btn--primary btn--md"
        type="button"
        disabled={busy}
        onClick={() => void enrol()}
      >
        {busy ? t('trainingsEnrolBusy') : t('trainingsEnrolAction')}
      </button>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
